import { useCallback, useRef } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { IdCard } from "lucide-react";
import { benvenuto, gruppoBenvenuto } from "@/data/benvenuto";

const INK = "#0b1220";
const GOLD = "#f59e0b";

const wrap = (x, testo, max) => {
  const out = [];
  let riga = "";
  testo.split(" ").forEach((w) => {
    const prova = riga ? `${riga} ${w}` : w;
    if (x.measureText(prova).width > max && riga) {
      out.push(riga);
      riga = w;
    } else riga = prova;
  });
  if (riga) out.push(riga);
  return out;
};

/**
 * Scheda personale da dare in mano il primo giorno: nome, PIN, QR d'ingresso e le tre cose
 * da sapere per il proprio ruolo. Si condivide o si salva come immagine sul telefono.
 */
export const SchedaStaff = ({ s, locale, url, lang = "it" }) => {
  const busy = useRef(false);

  const genera = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const b = benvenuto(lang);
      const gruppo = gruppoBenvenuto({ role: s.role });
      const punti = (b[gruppo === "titolare" ? "cassa" : gruppo].p || []).map((x) =>
        x.replace(/\*\*/g, "")
      );

      const W = 1080, H = 1500;
      const c = document.createElement("canvas");
      c.width = W;
      c.height = H;
      const x = c.getContext("2d");
      x.fillStyle = INK;
      x.fillRect(0, 0, W, H);
      const gr = x.createRadialGradient(W, 0, 40, W, 0, 900);
      gr.addColorStop(0, "rgba(245,158,11,.30)");
      gr.addColorStop(1, "rgba(245,158,11,0)");
      x.fillStyle = gr;
      x.fillRect(0, 0, W, H);

      x.fillStyle = GOLD;
      x.font = "bold 28px Arial";
      x.fillText((locale || "REPREHENDO").toUpperCase(), 80, 120);
      x.fillStyle = "#fff";
      x.font = "bold 78px Arial";
      x.fillText(`Ciao ${s.name.split(" ")[0]}`, 80, 220);
      x.fillStyle = "#cbd5e1";
      x.font = "38px Arial";
      x.fillText(s.role_label || s.role, 80, 275);

      // PIN in evidenza
      x.fillStyle = "rgba(255,255,255,.06)";
      x.fillRect(80, 320, 460, 150);
      x.fillStyle = GOLD;
      x.font = "bold 26px Arial";
      x.fillText("IL TUO PIN", 110, 372);
      x.fillStyle = "#fff";
      x.font = "bold 88px Arial";
      x.fillText(s.pin || "----", 110, 452);

      // QR d'ingresso
      if (url) {
        const img = new Image();
        img.src = await QRCode.toDataURL(url, { margin: 1, width: 520, color: { dark: INK, light: "#ffffff" } });
        await new Promise((r) => { img.onload = r; });
        x.fillStyle = "#fff";
        x.fillRect(600, 320, 400, 400);
        x.drawImage(img, 620, 340, 360, 360);
        x.fillStyle = "#94a3b8";
        x.font = "24px Arial";
        x.fillText("Inquadra per entrare", 620, 760);
      }

      x.fillStyle = GOLD;
      x.font = "bold 30px Arial";
      x.fillText("LE TRE COSE DA SAPERE", 80, 620);

      let y = 830;
      punti.slice(0, 3).forEach((testo, i) => {
        x.fillStyle = GOLD;
        x.font = "bold 44px Arial";
        x.fillText(`${i + 1}`, 80, y);
        x.fillStyle = "#e2e8f0";
        x.font = "34px Arial";
        wrap(x, testo, 860).slice(0, 4).forEach((riga, j) => {
          x.fillText(riga, 140, y + j * 46);
        });
        y += 190;
      });

      x.fillStyle = "#64748b";
      x.font = "26px Arial";
      x.fillText("Il PIN è personale: non si presta e non si scrive in giro.", 80, 1440);

      const blob = await new Promise((r) => c.toBlob(r, "image/png"));
      const file = new File([blob], `scheda-${s.name.split(" ")[0].toLowerCase()}.png`, { type: "image/png" });
      // Su telefono si apre il foglio di condivisione: da lì «Salva immagine» o WhatsApp.
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: `Scheda di ${s.name}` });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Scheda scaricata: stampala o mandala alla persona");
      }
    } catch {
      toast.error("Scheda non creata: riprova");
    } finally {
      busy.current = false;
    }
  }, [s, locale, url, lang]);

  return (
    <Button size="sm" variant="outline" onClick={genera} data-testid={`staff-card-${s.id}`}>
      <IdCard className="mr-1.5 h-3.5 w-3.5" /> Scheda personale
    </Button>
  );
};
